/* eslint-disable react/prop-types */
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Controller, useWatch, useFormContext } from 'react-hook-form';
import {
    FormControl,
    InputLabel,
    Select,
    Box,
    Checkbox,
    IconButton,
    InputAdornment,
    Tooltip,
    FormHelperText,
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { ClearIcon } from '@mui/x-date-pickers';

const buildTreeData = (flatList = [], rootParentId = '000000000000000') => {
    const nodeMap = new Map();
    const tree = [];

    flatList.forEach((item) => {
        if (!item || !item.AreaId) return;
        const id = String(item.AreaId);
        if (!nodeMap.has(id)) {
            nodeMap.set(id, {
                id,
                name: item.AreaName || item.Name || item.label || 'Unknown',
                levelId: item.LevelId,
                parentId: item.ParentId ? String(item.ParentId) : rootParentId,
                children: [],
            });
        }
    });

    flatList.forEach((item) => {
        if (!item || !item.AreaId) return;
        const id = String(item.AreaId);
        const parentId = item.ParentId ? String(item.ParentId) : rootParentId;
        const node = nodeMap.get(id);
        if (!node) return;

        if (parentId === rootParentId || !nodeMap.has(parentId)) {
            tree.push(node);
        } else {
            const parentNode = nodeMap.get(parentId);
            if (parentNode) {
                parentNode.children.push(node);
            }
        }
    });

    return tree;
};

const getAncestorAndNodeIds = (targetIds = [], flatList = [], rootParentId = '000000000000000') => {
    if (!targetIds || targetIds.length === 0 || !flatList || flatList.length === 0) return [];
    const itemMap = new Map();
    flatList.forEach((item) => {
        if (item && item.AreaId) {
            itemMap.set(String(item.AreaId), item);
        }
    });

    const toExpand = new Set();
    targetIds.forEach((id) => {
        const idStr = String(id);
        let curr = itemMap.get(idStr);
        if (curr) toExpand.add(idStr);
        while (curr && curr.ParentId && String(curr.ParentId) !== rootParentId && itemMap.has(String(curr.ParentId))) {
            const parentId = String(curr.ParentId);
            toExpand.add(parentId);
            curr = itemMap.get(parentId);
        }
    });

    return Array.from(toExpand);
};

const RHFTreeView = ({ formData, control, isDisabled }) => {
    const methods = useFormContext();
    const setValue = methods?.setValue || control?._setValue || control?.setValue;

    const fieldName = formData?.ValueExpr || formData?.DisplayExpr || 'Area';
    const idFieldName = `${fieldName}Ids`;

    const watchedIds = useWatch({ control, name: idFieldName });
    const watchedDisplayValue = useWatch({ control, name: fieldName }) || '';

    const [expandedItems, setExpandedItems] = useState([]);

    const rules = useMemo(() => {
        const customRequired = (formData?.Validations || []).find((v) => v.ValidationType === 'Required');
        return {
            required: customRequired?.ValidationMessage || 'Area is required',
        };
    }, [formData?.Validations]);

    const flatData = useMemo(() => formData?.DefaultValues || [], [formData?.DefaultValues]);

    const treeData = useMemo(() => buildTreeData(flatData), [flatData]);

    const selectedIds = useMemo(() => {
        if (Array.isArray(watchedIds)) return watchedIds.map(String);
        if (watchedIds) return [String(watchedIds)];
        return [];
    }, [watchedIds]);

    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    useEffect(() => {
        if (selectedIds && selectedIds.length > 0 && flatData.length > 0) {
            const neededExpansions = getAncestorAndNodeIds(selectedIds, flatData);
            if (neededExpansions.length > 0) {
                setExpandedItems((prev) => Array.from(new Set([...prev, ...neededExpansions])));
            }
        }
    }, [selectedIds, flatData]);

    const handleApplySelection = useCallback((newIds) => {
        const uniqueIds = Array.from(new Set(newIds));
        const selectedNames = uniqueIds.map((id) => {
            const item = flatData.find((d) => String(d.AreaId) === String(id));
            return item ? (item.AreaName || item.Name) : id;
        });
        const namesStr = selectedNames.join(', ');

        if (setValue) {
            setValue(idFieldName, uniqueIds, { shouldValidate: true, shouldDirty: true });
            setValue(fieldName, namesStr, { shouldValidate: true, shouldDirty: true });
        } else if (control?._setValue) {
            control._setValue(idFieldName, uniqueIds);
            control._setValue(fieldName, namesStr);
        }
    }, [flatData, setValue, control, idFieldName, fieldName]);

    const handleToggle = useCallback((nodeId, checked) => {
        const idStr = String(nodeId);
        let nextIds;
        if (checked) {
            nextIds = [...selectedIds, idStr];
        } else {
            nextIds = selectedIds.filter((id) => id !== idStr);
        }
        handleApplySelection(nextIds);
    }, [selectedIds, handleApplySelection]);

    const handleClear = useCallback((e) => {
        e.stopPropagation();
        handleApplySelection([]);
    }, [handleApplySelection]);

    const handleExpandedItemsChange = useCallback((event, itemIds) => {
        if (event) {
            event.stopPropagation();
        }
        setExpandedItems(itemIds);
    }, []);

    const renderTree = useCallback(
        (nodes) => nodes.map((node) => {
            const isSelected = selectedIdSet.has(String(node.id));
            return (
                <TreeItem
                    key={String(node.id)}
                    itemId={String(node.id)}
                    label={
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                py: 0.2,
                                borderRadius: '4px',
                                backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                px: isSelected ? 0.5 : 0,
                            }}
                        >
                            <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={isDisabled}
                                sx={{
                                    p: 0,
                                    mr: 0.5,
                                    transform: 'scale(0.8)',
                                    '& .MuiSvgIcon-root': { fontSize: '12px' },
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggle(node.id, !isSelected);
                                }}
                            />
                            <span
                                style={{
                                    fontSize: '15px',
                                    fontWeight: isSelected ? 600 : 'inherit',
                                    userSelect: 'none',
                                    color: isSelected ? '#1976d2' : 'inherit',
                                }}
                            >
                                {node.name}
                            </span>
                        </Box>
                    }
                    sx={{
                        '& .MuiTreeItem-label': {
                            fontSize: '15px',
                        },
                    }}
                >
                    {Array.isArray(node.children) && node.children.length > 0 && renderTree(node.children)}
                </TreeItem>
            );
        }),
        [selectedIdSet, isDisabled, handleToggle]
    );

    return (
        <Controller
            name={fieldName}
            control={control}
            rules={rules}
            render={({ field, fieldState: { error } }) => {
                const labelText = formData?.LabelName || formData?.DisplayExpr || 'Area';
                const displayStr = field.value || watchedDisplayValue || '';

                return (
                    <FormControl
                        fullWidth
                        size="small"
                        variant="outlined"
                        disabled={isDisabled}
                        sx={{
                            '& .MuiFormLabel-root': {
                                fontSize: '14px',
                            },
                        }}
                    >
                        <InputLabel
                            size="small"
                            shrink={Boolean(displayStr || selectedIds.length > 0)}
                            style={{ fontSize: '14px' }}
                            sx={{
                                '&.Mui-focused': { color: '#424242' },
                            }}
                        >
                            {labelText}
                            {rules?.required && (
                                <span style={{ color: isDisabled ? 'inherit' : 'red', marginLeft: '2px' }}>*</span>
                            )}
                        </InputLabel>
                        <Select
                            multiple
                            value={selectedIds}
                            label={labelText}
                            size="small"
                            style={{ fontSize: '13px' }}
                            disabled={isDisabled}
                            onChange={() => {}}
                            renderValue={() => {
                                if (!displayStr && selectedIds.length === 0) {
                                    return 'Select an area';
                                }
                                const displayValueShortened = displayStr.length > 35
                                    ? `${displayStr.slice(0, 35)}...`
                                    : displayStr;

                                return (
                                    <Tooltip
                                        title={
                                            <div
                                                style={{
                                                    maxHeight: '150px',
                                                    overflowY: 'auto',
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        color: '#9e9e9e',
                                                        fontSize: '12px',
                                                        fontWeight: '300',
                                                    }}
                                                >
                                                    {displayStr}
                                                </span>
                                            </div>
                                        }
                                        arrow
                                        placement="top"
                                        PopperProps={{
                                            sx: {
                                                '& .MuiTooltip-tooltip': {
                                                    backgroundColor: 'white',
                                                    color: 'black',
                                                    border: '1px solid #ccc',
                                                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 4px rgba(0, 0, 0, 0.25)',
                                                },
                                                '& .MuiTooltip-arrow': {
                                                    color: 'white',
                                                },
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                maxWidth: '100%',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {displayValueShortened}
                                        </Box>
                                    </Tooltip>
                                );
                            }}
                            endAdornment={
                                !isDisabled && selectedIds.length > 0 && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="clear selection"
                                            onClick={handleClear}
                                            edge="end"
                                            size="small"
                                            sx={{ marginRight: '18px', p: 0.25 }}
                                        >
                                            <ClearIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: '275px',
                                        overflowY: 'auto',
                                        fontSize: '13px',
                                    },
                                },
                            }}
                            sx={{
                                width: '100%',
                                '& .MuiSelect-root': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                },
                            }}
                        >
                            {treeData && treeData.length > 0 && (
                                <SimpleTreeView
                                    expandedItems={expandedItems}
                                    onExpandedItemsChange={handleExpandedItemsChange}
                                    aria-label="icon expansion"
                                    expansionTrigger="iconContainer"
                                    sx={{
                                        p: 0.5,
                                        '& .MuiTreeItem-groupTransition': {
                                            marginLeft: '14px !important',
                                            paddingLeft: '6px !important',
                                            borderLeft: '1px dashed #d0d7de',
                                        },
                                        '& .MuiTreeItem-content': {
                                            padding: '2px 4px !important',
                                            borderRadius: '4px',
                                            '&:hover': {
                                                backgroundColor: '#f6f8fa',
                                            },
                                        },
                                        '& .MuiTreeItem-label': {
                                            fontSize: '13px',
                                        },
                                    }}
                                >
                                    {renderTree(treeData)}
                                </SimpleTreeView>
                            )}
                        </Select>
                        {error && (
                            <FormHelperText sx={{ color: 'red', fontSize: '10px' }}>
                                {error.message}
                            </FormHelperText>
                        )}
                    </FormControl>
                );
            }}
        />
    );
};

export default RHFTreeView;
